/** Reuse presigned URLs until shortly before server-side expiry. */
export const PRESIGNED_URL_REFRESH_BUFFER_MS = 60_000;

type CachedStoredUrl = {
  url: string;
  expiresAtMs: number;
};

const cache = new Map<string, CachedStoredUrl>();

export function storedAttachmentUrlCacheKey(
  organizationId: number,
  attachmentId: string,
): string {
  return `${organizationId}:${attachmentId}`;
}

export function readCachedStoredAttachmentUrl(
  organizationId: number,
  attachmentId: string,
  now = Date.now(),
): CachedStoredUrl | null {
  const entry = cache.get(storedAttachmentUrlCacheKey(organizationId, attachmentId));
  if (!entry || isPresignedUrlExpiredAt(entry.expiresAtMs, now)) {
    return null;
  }
  return entry;
}

export function writeCachedStoredAttachmentUrl(
  organizationId: number,
  attachmentId: string,
  url: string,
  expiresAt: string,
): void {
  const expiresAtMs = Date.parse(expiresAt);
  if (!Number.isFinite(expiresAtMs)) {
    return;
  }
  cache.set(storedAttachmentUrlCacheKey(organizationId, attachmentId), {
    url,
    expiresAtMs,
  });
}

export function clearCachedStoredAttachmentUrl(
  organizationId: number,
  attachmentId: string,
): void {
  cache.delete(storedAttachmentUrlCacheKey(organizationId, attachmentId));
}

export function isPresignedUrlExpired(
  expiresAt: string,
  now = Date.now(),
  bufferMs = PRESIGNED_URL_REFRESH_BUFFER_MS,
): boolean {
  const expiresAtMs = Date.parse(expiresAt);
  if (!Number.isFinite(expiresAtMs)) {
    return true;
  }
  return isPresignedUrlExpiredAt(expiresAtMs, now, bufferMs);
}

export function isPresignedUrlExpiredAt(
  expiresAtMs: number,
  now = Date.now(),
  bufferMs = PRESIGNED_URL_REFRESH_BUFFER_MS,
): boolean {
  return expiresAtMs - bufferMs <= now;
}
